<template>
  <div>
    <Filters
      :sort-options="sortOptions"
      :current-sort="currentSort"
      :column-options="columnOptions"
      :visible-column-groups="visibleColumnGroups"
      :active-filters-for-export="activeFiltersForExport"
      :active-filters="activeFilters"
      :is-exporting="isExporting"
      :is-loading="isLoading"
      :total-items="totalItems"
      @update:sort="handleSortUpdate"
      @update:columns="handleColumnUpdate"
      @export="handleExport"
      @apply-filters="handleApplyFilters"
    />

    <div class="relative min-h-[500px]">
      <template v-if="isLoading">
        <ReportsTableSkeleton />
      </template>

      <template v-else>
        <ReportsTable
          v-if="reportData.length > 0"
          :data="reportData"
          :visible-columns="visibleColumns"
          :page-count="pageCount"
          :current-page="currentPage"
          @update:currentPage="handlePageUpdate"
        />
        <EmptyList v-else title="Nenhum relatório encontrado para os filtros selecionados" />
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import ReportsTable from './components/ReportsTable.vue'
import Filters from './components/Filters.vue'
import ReportsTableSkeleton from '@/components/designSystem/Skeleton/ReportsTableSkeleton.vue'
import EmptyList from '@/components/EmptyList.vue'
import { columnOptionsData, sortOptionsData } from './utils/Options'
import ReportsService from '@/Network/Services/ReportsService'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'

const headerStore = useHeaderStore()

const toast = useToast()
const { notifyError } = useErrorToast()
const isLoading = ref(true)
// web#3255: dono do estado de export em andamento. Desce por Filters até o
// ExportModal e bloqueia reclique mesmo com o modal fechado.
const isExporting = ref(false)
const reportData = ref([])
const sortOptions = ref(sortOptionsData)
const currentSort = ref('date_desc')
const columnOptions = ref(columnOptionsData)

const itemsPerPage = ref(10)
const currentPage = ref(1)
const totalItems = ref(0)

const activeFilters = ref({
  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .split('T')[0],
  endDate: new Date().toISOString().split('T')[0],
  companyIds: [],
  healthUnitIds: [],
  doctorIds: [],
  positionIds: [],
  operationalGroups: [],
  isChecked: undefined,
  paymentTiming: undefined,
  // `undefined` = todos. `cleanupFilters` remove a chave do payload da listagem e
  // o axios a omite da query da exportação; `false` sobrevive aos dois e filtra.
  hasException: undefined
})

const reportsService = new ReportsService()

onMounted(() => {
  headerStore.setTitle(
    'Relatórios',
    'Analise dados de plantões com filtros avançados, ordenação e exportação'
  )

  fetchReports()
})

watch([currentPage, currentSort], () => {
  fetchReports()
})

// eslint-disable-next-line complexity
const cleanupFilters = (filters) => {
  const cleaned = {}
  for (const key in filters) {
    const value = filters[key]
    if (value !== null && value !== undefined && value !== '') {
      if (Array.isArray(value) && value.length === 0) {
        continue
      }
      cleaned[key] = value
    }
  }

  const [field, type] = currentSort.value.split('_')

  if (field && type) {
    const fieldMap = {
      date: 'date',
      name: 'doctor',
      created: 'created_at'
    }

    const sortField = fieldMap[field]
    const sortType = type.toUpperCase()

    if (sortField && (sortType === 'ASC' || sortType === 'DESC')) {
      cleaned.sortField = sortField
      cleaned.sortType = sortType
    }
  }

  return cleaned
}

async function fetchReports() {
  isLoading.value = true
  try {
    const cleanedFilters = cleanupFilters(activeFilters.value)
    const filtersPayload = {
      ...cleanedFilters,
      page: currentPage.value,
      limit: itemsPerPage.value
    }
    const response = await reportsService.getReports(filtersPayload)
    reportData.value = response.data
    totalItems.value = response.total
  } catch (error) {
    console.error('Falha ao carregar os dados do relatório:', error)
    notifyError(error, 'Não foi possível carregar os dados do relatório.')
    reportData.value = []
    totalItems.value = 0
  } finally {
    isLoading.value = false
  }
}

const pageCount = computed(() => {
  return Math.ceil(totalItems.value / itemsPerPage.value)
})

const visibleColumns = computed(() => {
  return columnOptions.value.reduce((acc, col) => {
    acc[col.value] = col.visible
    return acc
  }, {})
})

const visibleColumnKeys = computed(() => {
  return columnOptions.value.filter((c) => c.visible).map((c) => c.value)
})

const visibleColumnGroups = computed(() => {
  return columnOptions.value.filter((c) => c.visible).map((c) => c.label)
})

function handlePageUpdate(page) {
  currentPage.value = page
}

function handleSortUpdate(value) {
  currentSort.value = value
}

function handleColumnUpdate(updatedColumns) {
  columnOptions.value = updatedColumns
}

function handleApplyFilters(newFilters) {
  currentPage.value = 1
  activeFilters.value = { ...newFilters }
  fetchReports()
}

async function handleExport(exportPayload) {
  if (isExporting.value) return
  isExporting.value = true
  try {
    const cleanedFilters = cleanupFilters(activeFilters.value)

    const columnsToExport = visibleColumnKeys.value

    const exportWithSortAndColumnsPayload = {
      ...exportPayload,
      sortField: cleanedFilters.sortField,
      sortType: cleanedFilters.sortType,
      columns: columnsToExport
    }

    const link = await reportsService.exportRelatoryXlsx(exportWithSortAndColumnsPayload)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(link.href)
    toast.success('Exportação iniciada com sucesso!')
  } catch (error) {
    console.error('Erro ao exportar o relatório:', error)
    notifyError(error, 'Não foi possível exportar o relatório.')
  } finally {
    isExporting.value = false
  }
}

const activeFiltersForExport = computed(() => {
  const filters = []
  if (activeFilters.value.isChecked !== undefined && activeFilters.value.isChecked !== null) {
    filters.push({ label: 'Possui Check-in', value: activeFilters.value.isChecked ? 'Sim' : 'Não' })
  }
  if (activeFilters.value.paymentTiming) {
    const timingMap = { FLOW: 'Fluxo', IMMEDIATE: 'À vista' }
    filters.push({
      label: 'Modalidade de Repasse',
      value: timingMap[activeFilters.value.paymentTiming]
    })
  }
  if (activeFilters.value.hasException !== undefined && activeFilters.value.hasException !== null) {
    filters.push({
      label: 'Excepcionalidade',
      value: activeFilters.value.hasException ? 'Com excepcionalidade' : 'Sem excepcionalidade'
    })
  }
  if (activeFilters.value.operationalGroups?.length) {
    filters.push({
      label: 'Grupos Operacionais',
      value: activeFilters.value.operationalGroups.join(', ')
    })
  }
  return filters
})
</script>
